import React, {
  memo,
  useState,
  useContext,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import queryString from 'query-string-for-all';
import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';
import { Panel } from '@prisjakt/ui/panel';
import NumberPagination from '@prisjakt/ui-number-pagination';
import AdContainer from '../../components/ads';
import ReportReviewModal from './components/report';
import LoginPromoModal from '../../components/modal/login-promo';
import { productReviewsListed, storeReviewsListed } from './tracking';
import * as actions from './actions';
import useReviewData from './hooks/data';
import { AppState } from '../../reducers';
import {
  UserReview as UserReviewBase,
  ProductUserReviews,
  StoreUserReviews,
  UserReviewTranslation,
} from '../../types';
import { Store, UserReview } from './types';
import { BelongsTo } from '../../components/user-review-card/types';

import {
  OldReviewsHeader,
  PanelTitle,
  SortedBy,
  UserReviewCard,
} from './styles';

function groupByDate(nodes: UserReviewBase[]) {
  const newReviewLimit = new Date();
  newReviewLimit.setFullYear(newReviewLimit.getFullYear() - 1);

  return nodes.reduce(
    (groups, review) => {
      const groupId =
        review.updatedAt &&
        review.createdAt &&
        new Date(review.updatedAt || review.createdAt) < newReviewLimit
          ? 'oldReviews'
          : 'reviews';
      return { ...groups, [groupId]: [...groups[groupId], review] };
    },
    { oldReviews: [], reviews: [] }
  );
}

type Translation = UserReviewTranslation & {
  text: UserReviewTranslation['reviewText'];
};

type OwnProps = {
  nodes: UserReview[];
  myReview?: UserReview | null;
  pageInfo: ProductUserReviews['pageInfo'] | StoreUserReviews['pageInfo'];
  belongsTo: BelongsTo;
  groupByOldAndNew?: boolean;
  showAds?: boolean;
  store?: Store;
  showPagination?: boolean;
  showTitle?: boolean;
  totalCount?: number;
};

type StateProps = {
  user: {
    id: string;
    displayName: string;
  } | null;
  isAuthenticated?: boolean;
};

type DispatchProps = {
  likeReviewToggle: typeof actions.likeReviewToggle;
};

export type Props = OwnProps & StateProps & DispatchProps;

const UserReviews = ({
  user,
  nodes,
  myReview,
  pageInfo,
  belongsTo,
  groupByOldAndNew,
  showAds = true,
  store,
  showPagination = true,
  showTitle = true,
  totalCount = 0,
}: Props) => {
  const [reportReview, setReportReview] = useState<UserReview | null>(null);
  const [showLoginPromo, setShowLoginPromo] = useState(false);
  const { reviews: currentReviews } = useReviewData({
    initialNodes: nodes,
    initialMyReview: myReview,
    pageInfo,
    pageType: belongsTo.type,
  });
  const { search } = useLocation();

  const containerElement = useRef<HTMLDivElement>(null);

  const { t } = useContext(AppContext);

  useEffect(() => {
    // Track reviews listed
    if (belongsTo.type === 'product') {
      productReviewsListed(pageInfo.sort?.name, { offset: pageInfo.offset });
    }
    if (belongsTo.type === 'store') {
      storeReviewsListed(pageInfo.sort?.name, { offset: pageInfo.offset });
    }
  }, [pageInfo.offset, pageInfo.sort, belongsTo.type]);

  const scrollToTop = useCallback(() => {
    if (
      containerElement.current &&
      'scrollIntoView' in containerElement.current
    ) {
      containerElement.current.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }, []);

  const onCompleteReport = useCallback(() => {
    setReportReview(null);
  }, []);

  const hideLoginPromo = useCallback(() => {
    setShowLoginPromo(false);
  }, []);

  const currentPage =
    pageInfo &&
    typeof pageInfo.offset === 'number' &&
    typeof pageInfo.limit === 'number'
      ? Math.floor(pageInfo.offset / pageInfo.limit) + 1
      : 1;

  const generatePaginationUrl = useCallback(
    (pageNumber: number) =>
      `?${queryString.stringify({
        ...queryString.parse(search.substring(1)),
        offset:
          pageInfo?.limit && pageNumber > 1
            ? (pageNumber - 1) * pageInfo.limit
            : undefined,
      })}`,
    [search, pageInfo?.limit]
  );

  const renderReview = (review: UserReview) => {
    if (!review.id) return null;

    const isMyReview = Boolean(
      user && review.user && user.id === review.user.id
    );

    return !review.underInvestigation && belongsTo && review.id ? (
      <UserReviewCard
        key={review.id}
        id={review.id}
        type={belongsTo.type}
        text={review.reviewText}
        rating={review.rating}
        purchaseDate={review.purchaseDate}
        translation={review.translation as Translation}
        reply={review.reply}
        replyDate={review.replyDate}
        createdAt={review.createdAtFromNow}
        updatedAt={review.updatedAtFromNow}
        userId={review.user && review.user.id}
        userAvatar={review.user && review.user.avatar}
        userDisplayName={review.user && review.user.displayName}
        userReviewCount={review.user && review.user.reviewCount}
        belongsTo={belongsTo}
        isMyReview={isMyReview}
        likes={review.likeScore}
        fullWidth
        store={store}
        hideStore
        viaShop={review.verifiedPurchase}
      />
    ) : null;
  };

  const { oldReviews, reviews } = groupByOldAndNew
    ? groupByDate(currentReviews)
    : { reviews: currentReviews, oldReviews: [] };

  return (
    <div ref={containerElement}>
      <LoginPromoModal
        isOpen={showLoginPromo}
        onRequestClose={hideLoginPromo}
      />

      <ReportReviewModal
        review={reportReview}
        onComplete={onCompleteReport}
        type={belongsTo.type}
      />

      <Panel>
        {showAds ? (
          <>
            <AdContainer
              placement="panorama_extra"
              format="panorama_1_wde"
              min={861}
              max={10000}
              scalable
              scaleBase=".pj-ui-panel--row"
              spacing={{ top: '10px', bottom: '10px' }}
            />

            <AdContainer
              placement="dubbelpanorama1"
              format="panorama_1"
              min={0}
              max={860}
              spacing={{ top: '10px', bottom: '10px' }}
            />
          </>
        ) : null}

        {belongsTo.type === 'product' && showTitle && reviews.length > 0 ? (
          <PanelTitle>{t('reviews', 'panel.title')}</PanelTitle>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {currentReviews.length > 0 && (
            <SortedBy>
              <T domain="reviews">info.sort-by-date</T>
            </SortedBy>
          )}
        </div>

        {reviews.length ? reviews.map(renderReview) : null}

        {oldReviews.length > 0 && (
          <OldReviewsHeader>
            <SortedBy>
              <T domain="reviews">info.sort-by-date</T>
            </SortedBy>
            <h5>
              <T domain="reviews">panel.old-title</T>
            </h5>
            <p>
              <T domain="reviews">panel.old-lead</T>
            </p>

            {oldReviews.map(renderReview)}
          </OldReviewsHeader>
        )}
        {showPagination ? (
          <NumberPagination
            currentPage={currentPage}
            totalPages={
              pageInfo.limit ? Math.ceil(totalCount / pageInfo.limit) : 1
            }
            linkToPage={generatePaginationUrl}
            onClick={scrollToTop}
          />
        ) : null}
      </Panel>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  if (!state.areas.account.me) {
    return {
      user: null,
      isAuthenticated: false,
    };
  }

  return {
    user: {
      id: state.areas.account.me.id,
      displayName: state.areas.account.me.displayName,
    },
    isAuthenticated: state.areas.account.isAuthenticated,
  };
};

const dispatchActions = {
  likeReviewToggle: actions.likeReviewToggle,
};

export default connect(mapStateToProps, dispatchActions)(memo(UserReviews));
